import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { num } from '../../format';
import { compareBom, downloadComparison, type Specification } from './api';

export function BomComparison({ spec }: { spec: Specification }) {
  const { t } = useTranslation();
  const released = spec.versions.filter((version) => version.status !== 'draft');
  const [open, setOpen] = useState(false);
  const [before, setBefore] = useState(released[0]?.id ?? '');
  const [after, setAfter] = useState(released.at(-1)?.id ?? '');
  const [differencesOnly, setDifferencesOnly] = useState(true);
  const [optional, setOptional] = useState(false);
  const options = { includeOptional: optional, differencesOnly };
  const comparison = useQuery({
    queryKey: ['bom-comparison', spec.id, before, after, optional, differencesOnly],
    queryFn: () => compareBom(spec.id, before, after, options),
    enabled: open && Boolean(before && after && before !== after),
  });
  const save = async () => {
    const blob = await downloadComparison(spec.id, before, after, options);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${spec.code ?? spec.name}-bom-comparison.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
  };
  if (released.length < 2) return null;
  return (
    <section className="bom-comparison">
      <button type="button" onClick={() => setOpen((value) => !value)}>{t('specs.compare.button')}</button>
      {open && <>
        <div className="bom-analysis__filters">
          <label>{t('specs.compare.before')} <VersionSelect versions={released} value={before} onChange={setBefore} /></label>
          <label>{t('specs.compare.after')} <VersionSelect versions={released} value={after} onChange={setAfter} /></label>
          <label><input type="checkbox" checked={differencesOnly} onChange={(event) => setDifferencesOnly(event.target.checked)} /> {t('specs.compare.differencesOnly')}</label>
          <label><input type="checkbox" checked={optional} onChange={(event) => setOptional(event.target.checked)} /> {t('specs.compare.includeOptional')}</label>
          <button type="button" disabled={!before || !after || before === after} onClick={() => void save()}>{t('specs.compare.export')}</button>
        </div>
        {before === after && <p className="panel__hint">{t('specs.compare.pickDifferent')}</p>}
        {comparison.isLoading && <p className="panel__hint">{t('common.loading')}</p>}
        {comparison.data && <>
          <h3>{t('specs.compare.structure')}</h3>
          <table className="spec__lines"><thead><tr><th>{t('specs.compare.state')}</th><th>{t('specs.part')}</th><th>{t('specs.compare.before')}</th><th>{t('specs.compare.after')}</th><th>{t('specs.compare.changes')}</th></tr></thead>
            <tbody>{comparison.data.rows.map((row, index) => <tr key={`${row.before?.id ?? row.after?.id}-${index}`}><td>{t(`specs.compare.states.${row.state}`)}</td><td>{row.after?.target_label ?? row.before?.target_label}</td><td>{row.before ? `${num(row.before.quantity)} · ${row.before.designators ?? '—'}` : '—'}</td><td>{row.after ? `${num(row.after.quantity)} · ${row.after.designators ?? '—'}` : '—'}</td><td>{row.changes.map((change) => t(`specs.compare.changesList.${change}`)).join(', ')}</td></tr>)}</tbody>
          </table>
          <h3>{t('specs.compare.exploded')}</h3>
          <table className="spec__lines"><thead><tr><th>{t('specs.compare.state')}</th><th>{t('specs.part')}</th><th>{t('specs.compare.before')}</th><th>{t('specs.compare.after')}</th></tr></thead>
            <tbody>{comparison.data.exploded.map((row) => <tr key={row.component_id}><td>{t(`specs.compare.states.${row.state}`)}</td><td>{row.internal_code} — {row.name}</td><td>{num(row.before)}</td><td>{num(row.after)}</td></tr>)}</tbody>
          </table>
        </>}
      </>}
    </section>
  );
}

function VersionSelect({ versions, value, onChange }: { versions: Specification['versions']; value: string; onChange: (value: string) => void }) {
  const { t } = useTranslation();
  return <select value={value} onChange={(event) => onChange(event.target.value)}>{versions.map((version) => <option key={version.id} value={version.id}>{t('specs.versionShort', { version: version.version })}</option>)}</select>;
}
